import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatQuantity } from '@abastece/shared';
import type { Producto, UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { ensenarCodigo, unidadesDe } from '../lib/catalogo.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { BuscadorProducto } from './BuscadorProducto.js';
import { Ico } from './Iconos.js';
import { Miniatura } from './Miniatura.js';

/**
 * «¿Qué producto es?»: a code the shop has never seen, saved on the product in the hand and on the
 * presentation it is printed on. The camera asks it, and so does the search box when a USB
 * scanner typed the code.
 *
 * The product first, by name. Then, for a product sold in more than one presentation, which one
 * the code is on: the case's code is printed on the case's wrapping and the bottle's on the
 * bottle, and a case's code saved as the bottle's rings up a case at the price of one bottle. So
 * it asks, the way the product page does, instead of settling on the smallest. The code on the
 * smallest presentation stays the product's own, as it always was.
 *
 * `onListo` gets the product, and the presentation when the code went on one of its own: what
 * the scan that brought the question here should now ring up. Given a `producto` already, a
 * suggestion picked, it starts at its presentations.
 */
export function EnsenarCodigo({
  codigo,
  onListo,
  producto: elegido = null,
}: {
  codigo: string;
  onListo: (p: Producto, unidad?: UnidadVenta) => void;
  producto?: Producto | null;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const [producto, setProducto] = useState<Producto | null>(elegido);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function guardar(p: Producto, unidad: UnidadVenta | undefined) {
    setError(null);
    setOcupado(true);
    try {
      onListo(p, await ensenarCodigo(p.id, unidad, unidadesDe(unidades, p.id), codigo));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('producto.noSePudoGuardar'));
    } finally {
      setOcupado(false);
    }
  }

  function elegirProducto(p: Producto) {
    if (unidadesDe(unidades, p.id).length > 1) setProducto(p);
    else void guardar(p, undefined);
  }

  if (producto) {
    const propias = unidadesDe(unidades, producto.id);
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Miniatura producto={producto} tamano="md" />
          <p className="min-w-0 flex-1 font-medium">{nombre(producto)}</p>
        </div>
        <p className="text-sm text-slate-400">{t('escaner.cualPresentacion', { codigo })}</p>
        <ul className="flex flex-col gap-2">
          {propias.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className="tarjeta-accion flex w-full items-center gap-3 p-3 text-left"
                disabled={ocupado}
                onClick={() => void guardar(producto, u)}
                data-sin-icono="fila"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{etiquetaUnidad(u.nombre, u.nombre)}</span>
                  <span className="tabular block text-xs text-slate-500">
                    {formatQuantity(u.factor)} {etiquetaUnidad(producto.unidadBase, producto.unidadBase)}
                  </span>
                </span>
                <Ico icono="adelante" className="size-4 shrink-0 text-slate-500" />
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn-texto gap-1.5 self-start text-sm text-slate-400" onClick={() => setProducto(null)}>
          <Ico icono="atras" className="size-4" />
          {t('escaner.otroProducto')}
        </button>
        {error && <p className="text-sm text-rose-400">{error}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <BuscadorProducto onElegir={elegirProducto} etiqueta={t('escaner.buscarProducto')} autoFocus escaner={false} />
      {error && <p className="text-sm text-rose-400">{error}</p>}
    </div>
  );
}
